// Small hooks shared across pages.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';
import { useT } from './i18n';
import type { TranslationKey } from '@shared/i18n/index';

/** Delays a value, so typing in a search box does not fire a request per key. */
export function useDebounced<T>(value: T, delay = 280): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/**
 * Runs `fn` whenever `deps` change, keeping the previous data visible while the
 * next result loads so tables do not blank out between keystrokes. Results that
 * arrive out of order are discarded.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [nonce, setNonce] = useState(0);
  const translateError = useErrorMessage();
  const latest = useRef(0);

  // `fn` is intentionally not a dependency: callers pass an inline closure, and
  // `deps` is the explicit statement of what the call actually depends on.
  const run = useCallback(() => {
    const ticket = ++latest.current;
    setLoading(true);
    fn().then(
      (result) => {
        if (ticket !== latest.current) return;
        setData(result);
        setError(undefined);
        setLoading(false);
      },
      (err: unknown) => {
        if (ticket !== latest.current) return;
        setError(translateError(err));
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  useEffect(run, [run]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}

/** Turns any thrown value into a message in the user's language. */
export function useErrorMessage() {
  const t = useT();
  return useCallback((err: unknown): string => {
    if (err instanceof ApiError) {
      // The server sends both a translated message and the key it came from;
      // prefer translating the key locally so the text matches the UI language.
      if (err.code) return t(err.code as TranslationKey);
      return err.message;
    }
    return t('error.unexpected');
  }, [t]);
}

/** Field-level errors from a failed submit, for inline display under inputs. */
export function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiError && err.fields ? err.fields : {};
}

/** Closes a popup when the user clicks outside it or presses Escape. */
export function useDismiss(active: boolean, onDismiss: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active, onDismiss]);
  return ref;
}
